import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Store, ExternalLink, Loader2, Save, Upload, X, ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { seller } from '@/lib/api';
import { getAuthToken } from '@/lib/auth-storage';
import { buildStorePath } from '@/lib/store-url';
import { toast } from 'sonner';

function displayLogoUrl(url) {
  if (!url) return '';
  if (url.startsWith('/uploads/')) return url.replace('/uploads/', '/api/uploads/');
  return url;
}

export default function SellerStoreProfileCard({ langCode = 'tr' }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState({
    store_name: '',
    store_description: '',
    store_logo: '',
    store_slug: '',
    product_count: 0,
  });

  useEffect(() => {
    let cancelled = false;
    seller
      .getStoreProfile()
      .then((res) => {
        if (!cancelled) {
          setForm({
            store_name: res.data.store_name || '',
            store_description: res.data.store_description || '',
            store_logo: res.data.store_logo || '',
            store_slug: res.data.store_slug || '',
            product_count: res.data.product_count || 0,
          });
        }
      })
      .catch(() => {
        if (!cancelled) toast.error(t('store.profileLoadError', 'Mağaza profili yüklenemedi'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [t]);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('store.logoTooLarge', 'Dosya boyutu 5MB\'dan küçük olmalıdır'));
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getAuthToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/upload-product-image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await response.json();
      setForm((prev) => ({ ...prev, store_logo: data.url }));
      toast.success(t('store.logoUploaded', 'Logo yüklendi'));
    } catch (err) {
      toast.error(err.message || t('store.logoUploadError', 'Logo yüklenemedi'));
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await seller.updateStoreProfile({
        store_name: form.store_name,
        store_description: form.store_description,
        store_logo: form.store_logo,
      });
      setForm((prev) => ({
        ...prev,
        store_slug: res.data.store_slug || prev.store_slug,
        product_count: res.data.product_count ?? prev.product_count,
      }));
      toast.success(t('store.profileSaved', 'Mağaza profili kaydedildi'));
    } catch {
      toast.error(t('store.profileSaveError', 'Kayıt başarısız'));
    } finally {
      setSaving(false);
    }
  };

  const storePath = form.store_slug ? buildStorePath(langCode, form.store_slug) : null;

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-primary/20" data-testid="seller-store-profile-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Store className="w-5 h-5 text-primary" />
          {t('store.myStore', 'Mağazam')}
        </CardTitle>
        <CardDescription>
          {t('store.myStoreDesc', 'Müşterilerin göreceği mağaza sayfanızı düzenleyin (logo, açıklama, kategoriler).')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 items-start">
          {/* Logo upload */}
          <div className="space-y-2">
            <Label>{t('store.logo', 'Mağaza logosu')}</Label>
            <div className="relative w-28 h-28 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/30 overflow-hidden flex items-center justify-center">
              {form.store_logo ? (
                <>
                  <img
                    src={displayLogoUrl(form.store_logo)}
                    alt={t('store.logoPreview', 'Logo önizleme')}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, store_logo: '' }))}
                    className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 shadow"
                    aria-label={t('store.removeLogo', 'Logoyu kaldır')}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : uploadingLogo ? (
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              ) : (
                <ImageIcon className="w-10 h-10 text-muted-foreground/50" />
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => handleLogoUpload(e.target.files?.[0])}
              data-testid="store-logo-upload-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-28"
              disabled={uploadingLogo}
              onClick={() => fileInputRef.current?.click()}
              data-testid="store-logo-upload-btn"
            >
              {uploadingLogo ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-1" />
                  {form.store_logo ? t('store.changeLogo', 'Değiştir') : t('store.uploadLogo', 'Logo yükle')}
                </>
              )}
            </Button>
            <p className="text-[11px] text-muted-foreground max-w-[7rem]">
              {t('store.logoHint', 'JPG, PNG, WEBP — max 5MB')}
            </p>
          </div>

          <div className="space-y-4 min-w-0">
            <div className="space-y-2">
              <Label>{t('store.storeName', 'Mağaza adı')}</Label>
              <Input
                value={form.store_name}
                onChange={(e) => setForm({ ...form, store_name: e.target.value })}
                placeholder={t('store.storeNamePlaceholder', 'Örn. Nil AVM')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('store.description', 'Mağaza açıklaması')}</Label>
              <Textarea
                value={form.store_description}
                onChange={(e) => setForm({ ...form, store_description: e.target.value })}
                rows={3}
                placeholder={t('store.descriptionPlaceholder', 'Mağazanızı kısaca tanıtın...')}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
          <div className="text-sm text-muted-foreground">
            {form.product_count} {t('store.activeProducts', 'aktif ürün')}
            {form.store_slug && (
              <span className="block text-xs mt-0.5 font-mono">/{langCode === 'tr' ? 'magaza' : 'store'}/{form.store_slug}</span>
            )}
          </div>
          <div className="flex gap-2">
            {storePath && (
              <Button variant="outline" size="sm" asChild>
                <Link to={storePath} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  {t('store.viewStore', 'Mağazayı gör')}
                </Link>
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={saving || uploadingLogo}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {t('common.save', 'Kaydet')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
